import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import Select from 'common-ui/select/Select';
import { getAdministrativeRegions, getCities } from 'features/newsales/newsalesActions';

import { ModalFormContent } from '../Addresses.styles';

const DistrictModal = ({ isOpen, onClose, onSubmit, district, countries, loading }) => {
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (district) {
      setName(district.name || '');
      if (district.countryId && countries) {
        const country = countries.find((c) => c.id === district.countryId);
        if (country) {
          setSelectedCountry({ value: country.id, label: country.name });
          const regionId = district.districtId || district.regionId;
          fetchRegions(district.countryId, regionId, district.cityId);
        }
      }
    } else {
      setName('');
      setSelectedCountry(null);
      setSelectedRegion(null);
      setSelectedCity(null);
      setRegions([]);
      setCities([]);
    }
    setError('');
  }, [district, countries, isOpen]);

  const fetchRegions = async (countryId, regionIdToSet = null, cityIdToSet = null) => {
    if (!countryId) {
      setRegions([]);
      return;
    }
    setLoadingRegions(true);
    try {
      const result = await dispatch(
        getAdministrativeRegions({
          countryId,
          limit: 1000,
        })
      );
      if (result.payload && result.payload.data) {
        setRegions(result.payload.data);
        const regionToSet =
          regionIdToSet || (district && (district.districtId || district.regionId));
        if (regionToSet) {
          const region = result.payload.data.find((r) => r.id === regionToSet);
          if (region) {
            setSelectedRegion({ value: region.id, label: region.name });
            fetchCities(countryId, regionToSet, cityIdToSet);
          }
        }
      }
    } catch (error) {
      setRegions([]);
    } finally {
      setLoadingRegions(false);
    }
  };

  const fetchCities = async (countryId, districtId, cityIdToSet = null) => {
    if (!countryId || !districtId) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    try {
      const result = await dispatch(
        getCities({
          countryId,
          districtId,
          limit: 1000,
        })
      );
      if (result.payload && result.payload.data) {
        setCities(result.payload.data);
        const cityToSet = cityIdToSet || (district && district.cityId);
        if (cityToSet) {
          const city = result.payload.data.find((c) => c.id === cityToSet);
          if (city) {
            setSelectedCity({ value: city.id, label: city.name });
          }
        }
      }
    } catch (error) {
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const handleCountryChange = (option) => {
    setSelectedCountry(option);
    setSelectedRegion(null);
    setSelectedCity(null);
    setError('');
    if (option) {
      fetchRegions(option.value);
    } else {
      setRegions([]);
      setCities([]);
    }
  };

  const handleRegionChange = (option) => {
    setSelectedRegion(option);
    setSelectedCity(null);
    setError('');
    if (option && selectedCountry) {
      fetchCities(selectedCountry.value, option.value);
    } else {
      setCities([]);
    }
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Administrative district name is required');
      return;
    }

    if (!selectedCountry) {
      setError('Please select a country');
      return;
    }

    if (!selectedRegion) {
      setError('Please select a region');
      return;
    }

    if (!selectedCity) {
      setError('Please select a city/village');
      return;
    }

    onSubmit({
      name: name.trim(),
      countryId: selectedCountry.value,
      districtId: selectedRegion.value,
      cityId: selectedCity.value,
    });
  };

  const handleClose = () => {
    setName('');
    setSelectedCountry(null);
    setSelectedRegion(null);
    setSelectedCity(null);
    setRegions([]);
    setCities([]);
    setError('');
    onClose();
  };

  const countryOptions = countries
    ? countries.map((country) => ({
        value: country.id,
        label: country.name,
      }))
    : [];

  const regionOptions = regions.map((region) => ({
    value: region.id,
    label: region.name,
  }));

  const cityOptions = cities.map((city) => ({
    value: city.id,
    label: city.name,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onOk={handleSubmit}
      title={district ? 'Edit Administrative District' : 'Create Administrative District'}
      okText={district ? 'Update' : 'Create'}
      cancelText="Cancel"
      footer
      closeIcon
      width="450px"
      onOkLoading={loading}
    >
      <ModalFormContent>
        <Select
          label="Country"
          req
          value={selectedCountry}
          onChange={handleCountryChange}
          options={countryOptions}
          placeholder="Select a country"
          isClearable
        />
        <Select
          label="Region"
          req
          value={selectedRegion}
          onChange={handleRegionChange}
          options={regionOptions}
          placeholder={
            loadingRegions
              ? 'Loading regions...'
              : selectedCountry
                ? 'Select a region'
                : 'Select a country first'
          }
          isClearable
          isDisabled={!selectedCountry || loadingRegions}
        />
        <Select
          label="City / Village"
          req
          value={selectedCity}
          onChange={(option) => {
            setSelectedCity(option);
            setError('');
          }}
          options={cityOptions}
          placeholder={
            loadingCities
              ? 'Loading cities...'
              : selectedRegion
                ? 'Select a city/village'
                : 'Select a region first'
          }
          isClearable
          isDisabled={!selectedRegion || loadingCities}
        />
        <Input
          label="Administrative District Name"
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError('');
          }}
          error={error}
          fullWidth
          placeholder="Enter administrative district name"
        />
      </ModalFormContent>
    </Modal>
  );
};

export default DistrictModal;
